// frontend/src/components/FloatingActionMenu.jsx
//
// Desktop-only expandable "+"/"x" toggle that reveals Share / Schedule a
// Visit / Call Us. Sits stacked above the standalone WhatsApp button
// rendered by ContactIcons.jsx (WhatsApp stays its own always-visible
// button, unchanged from before — this component only owns the toggle +
// its 3 expandable items).
import { useEffect, useRef, useState } from "react";
import { FaShareAlt, FaRegCalendarCheck, FaPhoneAlt, FaPlus, FaTimes } from "react-icons/fa";
import "./FloatingActionMenu.css";

export default function FloatingActionMenu({ projectName }) {
  const [open, setOpen] = useState(false);
  const [shareFeedback, setShareFeedback] = useState("");
  const menuRef = useRef(null);

  // Close on outside click / Escape, same UX as the site's other popups.
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const handleShare = async () => {
    const shareData = {
      title: projectName || document.title,
      text: projectName ? `Check out ${projectName}` : "Check out this project",
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        setShareFeedback("Link copied!");
        setTimeout(() => setShareFeedback(""), 1800);
      }
    } catch {
      // User cancelled the native share sheet — nothing to do.
    }
    setOpen(false);
  };

  // Both of these give the section the same shareable/bookmarkable URL
  // behavior as the navbar's real #id anchor links (see Navbar.jsx) —
  // pushState first so the address bar updates without the browser doing
  // its own instant jump, then our own smooth scroll takes over.
  const scrollToSection = (id) => {
    if (window.location.hash !== `#${id}`) {
      window.history.pushState(null, "", `#${id}`);
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  const handleScheduleVisit = () => scrollToSection("sitevisit");
  // ✅ Navigates to the Contact Us section (its Call Us / WhatsApp cards +
  // "Get a Call Back" button) instead of opening the call-back popup —
  // same navigation pattern as Schedule a Visit above.
  const handleCall = () => scrollToSection("contactus");

  return (
    <div className="fab-menu" ref={menuRef}>
      <div className={`fab-menu__items ${open ? "is-open" : ""}`} aria-hidden={!open}>
        <button type="button" className="fab-menu__item" onClick={handleShare} tabIndex={open ? 0 : -1}>
          <span>{shareFeedback || "Share"}</span>
          <span className="fab-menu__icon fab-menu__icon--share"><FaShareAlt aria-hidden="true" /></span>
        </button>

        <button type="button" className="fab-menu__item" onClick={handleScheduleVisit} tabIndex={open ? 0 : -1}>
          <span>Schedule a Visit</span>
          <span className="fab-menu__icon fab-menu__icon--visit"><FaRegCalendarCheck aria-hidden="true" /></span>
        </button>

        <button type="button" className="fab-menu__item" onClick={handleCall} tabIndex={open ? 0 : -1}>
          <span>Call Us</span>
          <span className="fab-menu__icon fab-menu__icon--call"><FaPhoneAlt aria-hidden="true" /></span>
        </button>
      </div>

      <button
        type="button"
        className="fab-menu__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close contact options" : "Open contact options"}
        aria-expanded={open}
      >
        {open ? <FaTimes aria-hidden="true" /> : <FaPlus aria-hidden="true" />}
      </button>
    </div>
  );
}
